import { track } from '@vercel/analytics';

/**
 * Serviço de Analytics da Vercel para Casal Resolve
 * Implementa tracking completo da jornada do usuário e conversões
 */
class VercelAnalyticsService {
    constructor() {
        this.isEnabled = process.env.NODE_ENV === 'production';
        this.debugMode = process.env.NODE_ENV === 'development';
        
        if (this.debugMode) {
            console.log('🔍 Vercel Analytics Service inicializado em modo debug');
        }
    }

    /**
     * Wrapper para track com logs de debug
     */
    _track(eventName, properties = {}) {
        if (this.debugMode) {
            console.log('📊 Vercel Analytics Event:', eventName, properties);
        }
        
        if (this.isEnabled) {
            // Filtrar propriedades sensíveis
            const sanitizedProperties = this._sanitizeProperties(properties);
            track(eventName, sanitizedProperties);
        }
    }

    /**
     * Remove informações sensíveis dos eventos
     */
    _sanitizeProperties(properties) {
        const sanitized = { ...properties };
        
        // Remover dados sensíveis
        delete sanitized.password;
        delete sanitized.token;
        delete sanitized.email;
        delete sanitized.personalData;
        
        // Truncar valores muito longos
        Object.keys(sanitized).forEach(key => {
            if (typeof sanitized[key] === 'string' && sanitized[key].length > 255) {
                sanitized[key] = sanitized[key].substring(0, 255);
            }
        });
        
        return sanitized;
    }

    // ===== AUTENTICAÇÃO E ONBOARDING =====

    /**
     * Usuário visitou página de landing
     */
    trackLandingPageView(source = 'direct') {
        this._track('Landing Page Viewed', {
            source,
            timestamp: Date.now()
        });
    }

    /**
     * Usuário iniciou processo de registro
     */
    trackRegistrationStarted(source = 'landing') {
        this._track('Registration Started', {
            source,
            step: 'form_opened'
        });
    }

    /**
     * Usuário completou registro
     */
    trackRegistrationCompleted(userType = 'individual') {
        this._track('Registration Completed', {
            userType,
            timestamp: Date.now()
        });
    }

    /**
     * Usuário fez login
     */
    trackLogin(method = 'email') {
        this._track('User Login', {
            method,
            timestamp: Date.now()
        });
    }

    /**
     * Usuário fez logout
     */
    trackLogout(sessionDuration) {
        this._track('User Logout', {
            sessionDuration: Math.round(sessionDuration / 1000), // em segundos
            timestamp: Date.now()
        });
    }

    // ===== AVALIAÇÕES E CONFLITOS =====

    /**
     * Usuário iniciou uma avaliação
     */
    trackAssessmentStarted(assessmentType = 'relationship-health') {
        this._track('Assessment Started', {
            assessmentType,
            step: 1
        });
    }

    /**
     * Progresso em avaliação
     */
    trackAssessmentProgress(assessmentType, currentStep, totalSteps, timeSpent) {
        this._track('Assessment Progress', {
            assessmentType,
            currentStep,
            totalSteps,
            completionPercent: Math.round((currentStep / totalSteps) * 100),
            timeSpent: Math.round(timeSpent / 1000) // em segundos
        });
    }

    /**
     * Avaliação completada
     */
    trackAssessmentCompleted(assessmentType, score, recommendations, completionTime) {
        this._track('Assessment Completed', {
            assessmentType,
            score: parseFloat(score.toFixed(1)),
            recommendationsCount: recommendations,
            completionTime: Math.round(completionTime / 1000),
            completed: true
        });
    }

    /**
     * Conflito criado
     */
    trackConflictCreated(category, severity, source = 'manual') {
        this._track('Conflict Created', {
            category,
            severity: parseInt(severity),
            source,
            timestamp: Date.now()
        });
    }

    /**
     * Análise de sentimento iniciada
     */
    trackSentimentAnalysisStarted(conflictCategory) {
        this._track('Sentiment Analysis Started', {
            conflictCategory,
            feature: 'ai_analysis'
        });
    }

    /**
     * Análise de sentimento completada
     */
    trackSentimentAnalysisCompleted(sentiment, confidence, processingTime) {
        this._track('Sentiment Analysis Completed', {
            sentiment,
            confidence: parseFloat(confidence.toFixed(2)),
            processingTime: Math.round(processingTime),
            success: true
        });
    }

    /**
     * Conflito resolvido
     */
    trackConflictResolved(conflictId, category, resolution, timeToResolve) {
        this._track('Conflict Resolved', {
            category,
            resolution,
            timeToResolve: Math.round(timeToResolve / (1000 * 60)), // em minutos
            success: true
        });
    }

    // ===== CHATBOT E IA =====

    /**
     * Chatbot iniciado
     */
    trackChatbotStarted(context = 'general') {
        this._track('Chatbot Started', {
            context,
            feature: 'ai_chat'
        });
    }

    /**
     * Mensagem enviada no chatbot
     */
    trackChatbotMessage(messageCount, sessionDuration) {
        this._track('Chatbot Message Sent', {
            messageCount,
            sessionDuration: Math.round(sessionDuration / 1000),
            engagement: 'active'
        });
    }

    /**
     * Sessão de chatbot encerrada
     */
    trackChatbotSessionEnded(messageCount, sessionDuration, satisfaction = null) {
        this._track('Chatbot Session Ended', {
            messageCount,
            sessionDuration: Math.round(sessionDuration / 1000),
            satisfaction: satisfaction ? parseInt(satisfaction) : null,
            completed: true
        });
    }

    /**
     * IA gerou sugestão
     */
    trackAISuggestionGenerated(suggestionType, context) {
        this._track('AI Suggestion Generated', {
            suggestionType,
            context,
            feature: 'ai_suggestions'
        });
    }

    /**
     * Usuário aceitou sugestão da IA
     */
    trackAISuggestionAccepted(suggestionType, rating = null) {
        this._track('AI Suggestion Accepted', {
            suggestionType,
            rating: rating ? parseInt(rating) : null,
            action: 'accepted'
        });
    }

    // ===== RELACIONAMENTO E CASAL =====

    /**
     * Convite para parceiro enviado
     */
    trackPartnerInviteSent() {
        this._track('Partner Invite Sent', {
            feature: 'couple_connection',
            action: 'invite'
        });
    }

    /**
     * Convite para parceiro aceito
     */
    trackPartnerInviteAccepted() {
        this._track('Partner Invite Accepted', {
            feature: 'couple_connection',
            action: 'accepted',
            milestone: 'couple_connected'
        });
    }

    /**
     * Exercício de casal iniciado
     */
    trackCoupleExerciseStarted(exerciseType) {
        this._track('Couple Exercise Started', {
            exerciseType,
            feature: 'couple_activities'
        });
    }

    /**
     * Exercício de casal completado
     */
    trackCoupleExerciseCompleted(exerciseType, duration, rating) {
        this._track('Couple Exercise Completed', {
            exerciseType,
            duration: Math.round(duration / 1000),
            rating: parseInt(rating),
            completed: true
        });
    }

    // ===== RECURSOS E FERRAMENTAS =====

    /**
     * Recurso acessado
     */
    trackResourceAccessed(resourceType, category) {
        this._track('Resource Accessed', {
            resourceType,
            category,
            action: 'view'
        });
    }

    /**
     * Ferramenta utilizada
     */
    trackToolUsed(toolType, duration, completed = true) {
        this._track('Tool Used', {
            toolType,
            duration: Math.round(duration / 1000),
            completed,
            engagement: 'active'
        });
    }

    /**
     * Download de recurso
     */
    trackResourceDownloaded(resourceType, format = 'pdf') {
        this._track('Resource Downloaded', {
            resourceType,
            format,
            action: 'download'
        });
    }

    // ===== ANALYTICS E INSIGHTS =====

    /**
     * Página de analytics acessada
     */
    trackAnalyticsViewed(viewType = 'overview') {
        this._track('Analytics Viewed', {
            viewType,
            feature: 'analytics'
        });
    }

    /**
     * Relatório gerado
     */
    trackReportGenerated(reportType, format = 'pdf') {
        this._track('Report Generated', {
            reportType,
            format,
            feature: 'reporting'
        });
    }

    /**
     * Insight visualizado
     */
    trackInsightViewed(insightType, category) {
        this._track('Insight Viewed', {
            insightType,
            category,
            feature: 'ai_insights'
        });
    }

    // ===== CONVERSÕES E MILESTONES =====

    /**
     * Milestone de engajamento atingido
     */
    trackEngagementMilestone(milestone, value) {
        this._track('Engagement Milestone', {
            milestone,
            value: parseInt(value),
            achieved: true
        });
    }

    /**
     * Progresso significativo detectado
     */
    trackProgressMilestone(milestoneType, level, pointsEarned = 0) {
        this._track('Progress Milestone', {
            milestoneType,
            level: parseInt(level),
            pointsEarned: parseInt(pointsEarned),
            achievement: 'milestone'
        });
    }

    /**
     * Meta pessoal atingida
     */
    trackPersonalGoalAchieved(goalType, targetValue, actualValue) {
        this._track('Personal Goal Achieved', {
            goalType,
            targetValue: parseFloat(targetValue),
            actualValue: parseFloat(actualValue),
            achievement: 'goal_completed'
        });
    }

    // ===== PERFORMANCE E EXPERIÊNCIA =====

    /**
     * Tempo de carregamento de página com métricas detalhadas
     */
    trackPageLoadTime(page, loadTime, device = 'unknown', additionalMetrics = {}) {
        if (loadTime > 100) { // só trackear se relevante
            this._track('Page Load Performance', {
                page,
                loadTime: Math.round(loadTime),
                device,
                performance: loadTime > 3000 ? 'slow' : loadTime > 1000 ? 'medium' : 'fast',
                ...additionalMetrics
            });
        }
    }

    /**
     * Core Web Vitals tracking manual (complementa Speed Insights)
     */
    trackWebVitals(metrics) {
        this._track('Web Vitals', {
            lcp: metrics.lcp ? Math.round(metrics.lcp) : null,
            fid: metrics.fid ? Math.round(metrics.fid) : null,
            cls: metrics.cls ? Math.round(metrics.cls * 1000) / 1000 : null,
            fcp: metrics.fcp ? Math.round(metrics.fcp) : null,
            ttfb: metrics.ttfb ? Math.round(metrics.ttfb) : null,
            measurementType: 'manual'
        });
    }

    /**
     * Performance de recursos específicos
     */
    trackResourcePerformance(resourceType, loadTime, size = null) {
        this._track('Resource Performance', {
            resourceType,
            loadTime: Math.round(loadTime),
            size: size ? Math.round(size / 1024) : null, // em KB
            performance: loadTime > 2000 ? 'slow' : loadTime > 500 ? 'medium' : 'fast'
        });
    }

    /**
     * Erro de JavaScript capturado
     */
    trackJavaScriptError(errorMessage, page, component = null) {
        this._track('JavaScript Error', {
            errorType: 'client_error',
            page,
            component,
            severity: 'error'
        });
    }

    /**
     * Feedback do usuário
     */
    trackUserFeedback(rating, category, page) {
        this._track('User Feedback', {
            rating: parseInt(rating),
            category,
            page,
            feedback: 'submitted'
        });
    }

    // ===== FUNIS DE CONVERSÃO =====

    /**
     * Funil: Visitante → Usuário Registrado
     */
    trackConversionFunnelStep(funnel, step, stepData = {}) {
        this._track(`Funnel ${funnel} Step ${step}`, {
            funnel,
            step: parseInt(step),
            ...stepData,
            funnelTracking: true
        });
    }

    /**
     * Funil principal: Descoberta → Engajamento → Retenção
     */
    trackMainFunnel = {
        discovery: (source) => this.trackConversionFunnelStep('main', 1, { stage: 'discovery', source }),
        assessment: () => this.trackConversionFunnelStep('main', 2, { stage: 'assessment' }),
        firstConflict: () => this.trackConversionFunnelStep('main', 3, { stage: 'first_conflict' }),
        aiFeatures: () => this.trackConversionFunnelStep('main', 4, { stage: 'ai_features' }),
        coupleConnection: () => this.trackConversionFunnelStep('main', 5, { stage: 'couple_connection' }),
        activeUser: () => this.trackConversionFunnelStep('main', 6, { stage: 'active_user' })
    };

    // ===== UTILITÁRIOS =====

    /**
     * Obtém informações do dispositivo
     */
    getDeviceInfo() {
        const userAgent = navigator.userAgent;
        const isMobile = /Mobile|Android|iPhone|iPad/.test(userAgent);
        const isTablet = /iPad|Tablet/.test(userAgent);
        
        return {
            device: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
            userAgent: userAgent.substring(0, 100) // truncar para não exceder limite
        };
    }

    /**
     * Cria sessão de tracking com timer
     */
    startSession(sessionType = 'general') {
        const startTime = Date.now();
        
        return {
            end: () => {
                const duration = Date.now() - startTime;
                this._track('Session Ended', {
                    sessionType,
                    duration: Math.round(duration / 1000),
                    device: this.getDeviceInfo().device
                });
                return duration;
            }
        };
    }

    /**
     * Limpa dados de debug
     */
    clearDebugData() {
        if (this.debugMode) {
            console.log('🧹 Dados de debug do Vercel Analytics limpos');
        }
    }

    /**
     * Habilita/desabilita tracking
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
        if (this.debugMode) {
            console.log(`📊 Vercel Analytics ${enabled ? 'habilitado' : 'desabilitado'}`);
        }
    }
}

export default new VercelAnalyticsService();